/*
 * _customled-cart-accessory-add.js  (Maranello M1.10.x)
 *
 * Intercepts submission of the "Add All to Cart" form rendered by
 * snippets/_customled-accessory-grid.liquid and mirrors Maranello's
 * native <product-form> AJAX flow (assets/product-form.js):
 *
 *   1. POST to /cart/add.js with the multi-item form payload,
 *      plus Maranello's section-rendering params so the cart drawer
 *      contents can be re-rendered in one round trip.
 *   2. Call minicart.renderContents(response) on success.
 *   3. Honor the theme's `redirectToCart` setting:
 *        true  -> location.href = routes.cart_url (full page nav)
 *        false -> minicart.open(button) so the cart drawer slides in,
 *                 matching every other ADD TO CART button on the site.
 *   4. Publish the standard cartItemAdd event so the badge counter,
 *      analytics hooks, and any cart-update listeners react.
 *
 * Falls back to normal form POST (browser default) if <mini-cart> isn't
 * on the page (defensive — should always be present in Maranello).
 *
 * Page-gated: loaded from snippets/_customled-stylesheets.liquid on
 * product templates only.
 */
/* Build marker: CUSTOMLED-M1.10.x-CAPTURE-PHASE */
(function () {
  "use strict";

  function isOurForm(form) {
    if (!form || !form.classList) return false;
    return (
      form.classList.contains("customled-accessory-grid__add-all-form") ||
      form.classList.contains("customled-accessory-add-single-form")
    );
  }

  function init() {
    // Capture-phase CLICK listener handles BOTH paths:
    //   1. The PDP "Add All" form's submit button (form-based, kept
    //      because it batches multiple variants in one request).
    //   2. The cart drawer's per-tile "+ Add" button — a plain
    //      <button> with data-customled-add-variant-id. No <form>
    //      element so Facebook Pixel doesn't auto-submit it.
    document.addEventListener(
      "click",
      function (e) {
        if (!e.target.closest) return;
        var addAllBtn = e.target.closest("[data-customled-add-all-variants]");
        if (addAllBtn) {
          e.preventDefault();
          e.stopImmediatePropagation();
          handleAddAllFromData(e, addAllBtn);
          return;
        }
        var directAddBtn = e.target.closest("[data-customled-add-variant-id]");
        if (directAddBtn) {
          e.preventDefault();
          e.stopImmediatePropagation();
          handleSingleAdd(e, directAddBtn);
          return;
        }
        var btn = e.target.closest('button[type="submit"]');
        if (!btn) return;
        var form = btn.form || btn.closest("form");
        if (!isOurForm(form)) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        handleAddAll(e, form);
      },
      true
    );

    // Belt-and-braces capture-phase SUBMIT listener for the PDP "Add
    // All" form — catches Enter-key submits and any programmatic
    // form.submit() (e.g. Facebook Pixel) that doesn't go through the
    // button click path.
    document.addEventListener(
      "submit",
      function (e) {
        var form = e.target;
        if (!isOurForm(form)) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        handleAddAll(e, form);
      },
      true
    );
  }

  // Single-item add path for the cart drawer's "+ Add" tile buttons.
  // Builds a FormData payload from the button's data attributes and
  // dispatches the same AJAX flow as the "Add All" form handler.
  function handleSingleAdd(e, btn) {
    var fakeForm = document.createElement("form");
    var idInput = document.createElement("input");
    idInput.name = "id";
    idInput.value = btn.getAttribute("data-customled-add-variant-id");
    fakeForm.appendChild(idInput);
    var qtyInput = document.createElement("input");
    qtyInput.name = "quantity";
    qtyInput.value = btn.getAttribute("data-customled-add-quantity") || "1";
    fakeForm.appendChild(qtyInput);
    fakeForm.__customledTriggerBtn = btn;
    handleAddAll(e, fakeForm);
  }

  // Multi-item add path for the cart drawer's "Add All to Cart" button.
  // Reads a comma-separated list of variant IDs from the button's data
  // attribute and fans them out into the items[][...] FormData payload.
  function handleAddAllFromData(e, btn) {
    var raw = btn.getAttribute("data-customled-add-all-variants") || "";
    var ids = raw
      .split(",")
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
    if (ids.length === 0) return;
    var fakeForm = document.createElement("form");
    for (var i = 0; i < ids.length; i++) {
      var idInput = document.createElement("input");
      idInput.name = "items[][id]";
      idInput.value = ids[i];
      fakeForm.appendChild(idInput);
      var qtyInput = document.createElement("input");
      qtyInput.name = "items[][quantity]";
      qtyInput.value = "1";
      fakeForm.appendChild(qtyInput);
    }
    fakeForm.__customledTriggerBtn = btn;
    handleAddAll(e, fakeForm);
  }

  function handleAddAll(e, form) {
    var minicart = document.querySelector("mini-cart");
    if (!minicart || typeof minicart.open !== "function") {
      return;
    }

    // For real forms we query the submit button; for the synthetic
    // single-add form we use the trigger stashed by handleSingleAdd.
    var btn =
      form.__customledTriggerBtn ||
      form.querySelector('button[type="submit"]');
    if (btn && btn.classList.contains("loading")) return;

    // Capture state we'll need to restore on error/catch paths AND
    // schedule an independent revert timer for the success flash.
    // Read origLabel BEFORE we add the loading class so the loading-
    // state text doesn't get captured as the "original."
    var isQuickAdd =
      btn &&
      btn.classList &&
      btn.classList.contains("customled-accessory-grid__quickadd-btn");
    var origLabel = isQuickAdd ? btn.textContent : null;

    // Restore helper used by error / catch paths AND by the success-
    // flash revert. Idempotent: safe to call even if no flash state
    // was applied. Skips silently if the button has been detached
    // (which happens on cart-drawer re-render after a successful add).
    function restoreQuickAddState() {
      if (!isQuickAdd || !btn) return;
      if (!document.body.contains(btn)) return;
      btn.classList.remove("customled-quickadd-added");
      if (origLabel != null) {
        btn.textContent = origLabel;
      }
    }

    if (btn) {
      btn.classList.add("loading");
      btn.setAttribute("aria-disabled", "true");
    }

    var formData = new FormData(form);

    // Section-rendering payload — same trick product-form.js uses to
    // update the drawer contents without a second request.
    if (typeof minicart.getSectionsToRender === "function") {
      try {
        var sections = minicart
          .getSectionsToRender()
          .map(function (s) {
            return s.section;
          });
        formData.append("sections", sections.join(","));
        formData.append("sections_url", window.location.pathname);
      } catch (err) {
        // Non-fatal — proceed without section payload; the drawer will
        // self-refresh on its next update cycle.
        // eslint-disable-next-line no-console
        console.warn("[customled-accessory-add-all] section payload skipped:", err);
      }
    }

    var url =
      (window.routes && window.routes.cart_add_url) || "/cart/add.js";

    fetch(url, {
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        Accept: "application/json",
      },
      body: formData,
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (response) {
        if (response && response.status) {
          // Shopify error payload (e.g. 422 sold-out). Restore button
          // state — the flash hasn't been applied yet on this path,
          // but the helper is idempotent so this is safe AND defends
          // against any future code that might set flash state before
          // the error branch is reached.
          // eslint-disable-next-line no-console
          console.warn(
            "[customled-accessory-add-all] cart error:",
            response.message || response.description || response
          );
          restoreQuickAddState();
          return;
        }

        // Capture click context BEFORE the cart drawer re-renders.
        // After renderContents() swaps the drawer body, btn is detached
        // and btn.closest("mini-cart") would return null even if the
        // click did originate inside the drawer. We need to know "was
        // the user already in the drawer when they clicked?" so we can
        // skip the redundant minicart.open() slide-in animation.
        var clickOriginatedInDrawer =
          btn && btn.closest && btn.closest("mini-cart") !== null;

        // The standard post-success flow: re-render the mini-cart with
        // the new line item(s), optionally redirect to /cart, otherwise
        // pop the drawer open. We wrap it so the per-tile QUICK ADD path
        // can defer it briefly to show a "✓ Added" flash on the button.
        var finalize = function () {
          if (typeof minicart.renderContents === "function") {
            minicart.renderContents(response);
          }

          if (window.themeSettings && window.themeSettings.redirectToCart) {
            window.location.href =
              (window.routes && window.routes.cart_url) || "/cart";
            return;
          }

          if (!clickOriginatedInDrawer) {
            minicart.open(btn);
          }

          if (
            typeof window.publish === "function" &&
            window.PUB_SUB_EVENTS &&
            window.PUB_SUB_EVENTS.cartItemAdd
          ) {
            window.publish(window.PUB_SUB_EVENTS.cartItemAdd, {
              source: "customled-accessory-add-all",
              items: [response],
            });
          }
        };

        // QUICK ADD per-tile button: brief "✓ Added" flash so the
        // customer sees their click registered before the cart drawer
        // re-renders (cart-drawer context) or before the drawer slides
        // in over the page (PDP context). On PDP the button persists
        // post-render, so we also schedule an INDEPENDENT revert timer
        // so the customer can add the same accessory again if they
        // want. The revert timer must NOT be nested inside the
        // finalize() callback — if finalize() throws (e.g.
        // renderContents fails for any reason), a nested revert would
        // never schedule and the button would be frozen at "✓ Added"
        // permanently. Decoupled timers guarantee revert.
        if (isQuickAdd) {
          btn.classList.add("customled-quickadd-added");
          btn.textContent = "✓ Added";
          setTimeout(function () {
            try {
              finalize();
            } catch (finalizeErr) {
              // eslint-disable-next-line no-console
              console.error(
                "[customled-accessory-add-all] finalize threw:",
                finalizeErr
              );
            }
          }, 450);
          // Independent revert: 1950ms = 450ms flash + 1500ms hold.
          // Runs even if finalize() above threw.
          setTimeout(restoreQuickAddState, 1950);
        } else {
          finalize();
        }
      })
      .catch(function (err) {
        // eslint-disable-next-line no-console
        console.error("[customled-accessory-add-all]", err);
        restoreQuickAddState();
      })
      .finally(function () {
        if (btn) {
          btn.classList.remove("loading");
          btn.removeAttribute("aria-disabled");
        }
      });
  }

  // CUSTOMLED M1.10.x: keep the cart-drawer header label
  // (.customled-mini-cart-header-label, rendered by header-mini-cart.liquid
  // as "N items in cart, M recommended accessories") in sync with two
  // independent server-rendered sources:
  //
  //   1. cart.item_count — read from the .head-slot-cart-link-quantity
  //      badge inside .head-mast .head-slot-cart-link. Maranello's
  //      MiniCart.getSectionsToRender() in scripts.js refreshes this
  //      element on every cart update.
  //
  //   2. RA tile count — read from the data-customled-ra-count attribute
  //      on .customled-cart-drawer-accessories, set server-side by
  //      _customled-cart-drawer-accessories.liquid from the same union-
  //      and-dedupe pass that produces the rendered tiles. Refreshed
  //      via the header-mini-cart-content section render.
  //
  // Maranello's MiniCart.renderContents() does NOT refresh our h4 inside
  // .mini-cart-header (it lives in the same header section but outside
  // the section-render selector `.head-mast .head-slot-cart-link`). So
  // we mirror both counts into the h4 ourselves with a MutationObserver
  // pair watching both source elements. Works regardless of who
  // triggered the cart update — Maranello's own ATC, our accessory
  // ATC, quantity adjusters, or any future code path.
  function syncMiniCartHeaderLabel() {
    var label = document.querySelector(".customled-mini-cart-header-label");
    if (!label) return;

    // Source 1: item count from the cart-icon badge. When cart is
    // empty, the badge is omitted entirely (see header-mini-cart.liquid
    // `{%- if cart != empty -%}` guard). Treat absence as 0.
    var badge = document.querySelector(".head-slot-cart-link-quantity");
    var itemCount = 0;
    if (badge) {
      var parsedItems = parseInt((badge.textContent || "").trim(), 10);
      itemCount = isNaN(parsedItems) ? 0 : parsedItems;
    }

    // Source 2: RA tile count from the data attribute on the RA section.
    // When no tiles survive the filters, the entire section isn't
    // rendered server-side (filtered_count == 0 guard in the snippet),
    // so the element is absent and we treat as 0.
    var raSection = document.querySelector(".customled-cart-drawer-accessories");
    var raCount = 0;
    if (raSection) {
      var parsedRa = parseInt(
        raSection.getAttribute("data-customled-ra-count") || "0",
        10
      );
      raCount = isNaN(parsedRa) ? 0 : parsedRa;
    }

    // Compose the label. Singular/plural inline for both halves. Hide
    // the RA clause entirely when raCount is 0 so the empty case still
    // reads cleanly ("3 items in cart" not "3 items in cart, 0 ...").
    var itemPart = itemCount === 1 ? "1 item in cart" : itemCount + " items in cart";
    var raPart = "";
    if (raCount === 1) {
      raPart = ", 1 recommended accessory";
    } else if (raCount > 1) {
      raPart = ", " + raCount + " recommended accessories";
    }
    var nextText = itemPart + raPart;
    if (label.textContent.trim() !== nextText) {
      label.textContent = nextText;
    }
  }

  function initHeaderCountSync() {
    // Initial paint already came from Liquid render of header-mini-
    // cart.liquid, but that only knew item count (not RA count). Sync
    // once on init so the RA part appears immediately on first paint.
    syncMiniCartHeaderLabel();

    // Observer A: cart icon link. Maranello swaps this element's inner
    // HTML on cart updates (Section Rendering API → cart icon badge).
    var slot = document.querySelector(".head-mast .head-slot-cart-link");
    if (slot) {
      new MutationObserver(syncMiniCartHeaderLabel).observe(slot, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // Observer B: cart-drawer body container. Maranello swaps the inner
    // HTML of #header-mini-cart-content on cart updates (Section
    // Rendering API → drawer body, which includes the RA section). We
    // need to re-sync after this swap because the RA count is read
    // from the new DOM. Both observers fire on the same renderContents
    // pass — the redundant sync is cheap (text-equality short-circuit
    // in the function above) and guarantees the label reflects the
    // freshest server state.
    var body = document.getElementById("header-mini-cart-content");
    if (body) {
      new MutationObserver(syncMiniCartHeaderLabel).observe(body, {
        childList: true,
        subtree: true,
      });
    }
  }

  function bootstrap() {
    init();
    initHeaderCountSync();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap);
  } else {
    bootstrap();
  }
})();
